import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUI } from '@/store/ui'
import { useSelection } from '@/store/selection'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { Dossier } from '@/components/Dossier'

// The constellation's phone posture: no galaxy, no sim — search the sky, open a
// Dossier. Distance = detail still holds; on a phone the detail IS the dossier.

export function ConstellationSearch() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const { selected, select } = useSelection()
  const [q, setQ] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['graph-network', 'all'],
    queryFn: () => api.network('all'),
  })

  const results = useMemo(() => {
    const nodes = (data?.nodes ?? []).filter((n) => !n.self)
    const needle = q.trim().toLowerCase()
    const hits = needle
      ? nodes.filter((n) => n.name.toLowerCase().includes(needle))
      : [...nodes].sort((a, b) => (b.heat ?? 0) - (a.heat ?? 0) || (b.score ?? 0) - (a.score ?? 0))
    return hits.slice(0, 40)
  }, [data, q])

  function open(n: GraphNode) {
    select({
      id: n.id, name: n.name, type: n.type, world: n.world,
      score: n.score, domain: n.domain, attributes: n.attributes ?? null,
    })
  }

  if (selected) {
    return (
      <div className="dw-page-narrow">
        <button type="button" className="dw-back-link" onClick={() => select(null)}>
          ← back to search
        </button>
        <Dossier />
      </div>
    )
  }

  return (
    <div className="dw-page-narrow">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">Constellation</div>
          <div className="dw-page-subtitle">
            The full sky lives on a bigger screen — here, search it.
          </div>
        </div>
      </div>
      <input
        className="dw-input"
        placeholder="Search your stars…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      {isLoading && <p className="dw-empty">Loading…</p>}
      <div style={{ display: 'grid', gap: 6, marginTop: 12 }}>
        {results.map((n) => (
          <button key={n.id} type="button" className="dw-queue-row" onClick={() => open(n)}>
            <span className="dw-queue-glyph">{(n.heat ?? 0) > 0 ? '●' : '✧'}</span>
            <span className="dw-queue-title">
              {n.name}
              <span className="dw-inbox-detail">
                {n.type} · {n.domain}
                {(n.heat ?? 0) > 0 && ` · ${n.heat} fresh`}
              </span>
            </span>
          </button>
        ))}
        {!isLoading && results.length === 0 && (
          <div className="dw-rail-empty">No star answers to that name.</div>
        )}
      </div>
    </div>
  )
}
